import { ComponentUsageMap, ComponentRegistry as ComponentRegistryType } from '../types/state.types';
import { WidgetType } from '../types/common.types';
import { containerStore } from '../utils/containerRegistryBridge';
import {CONNECTOR} from "../../ISRConstants";

type ComponentLocation = {
  containerId: string;
  pageId: string;
  groupId: string;
}

type ComponentDefinition = {
  id: string;
  name: string;
  type: 'submodel' | 'field' | 'category' | 'connector' | 'widget';
  isReusable: boolean;
  fieldType?: string;
  parentId?: string;
  widgetType?: WidgetType;
  dataConfiguration?: any;
}

export class ComponentRegistry {
  private modelSpec: any;
  private componentDefinitions: Map<string, ComponentDefinition>;
  private usageMap: ComponentUsageMap;
  private registry: ComponentRegistryType;
  private containerSubmodels: Map<string, string> = new Map();
  private globalSubmodelUsage: Map<string, boolean> = new Map();
  private containerWidgets: Map<string, Map<string, number>> = new Map();
  private moveableWidgetConfigs: Map<string, any> = new Map();

  private containerHierarchy: Map<string, string> = new Map();
  private pageYourBuildWidget: Map<string, {containerId: string, widgetId: string}> = new Map();

  constructor(modelSpec: any) {
    this.modelSpec = modelSpec;
    this.componentDefinitions = new Map();
    this.usageMap = {};
    this.registry = {
      subModels: {},
      widgets: {}
    };
    
    this.initializeFromModelSpec();
  }

  private initializeFromModelSpec(): void {
    
    if (!this.modelSpec || !this.modelSpec.project || !this.modelSpec.project.dataModels) {
        console.warn('No model spec provided for component registry initialization');
        return;
    }

    const dataModels = this.modelSpec.project.dataModels;
    
    if (!Array.isArray(dataModels)) {
        console.warn('dataModels is not an array');
        return;
    }

    dataModels.forEach(submodel => {
        
        const submodelId = submodel.id;
        const submodelName = submodel.data?.name || submodel.name;
        
        this.registerComponent({
            id: submodelId,
            name: submodelName,
            type: 'submodel',
            isReusable: false
        });

        this.registry.subModels[submodelId] = {
            isReusable: false,
            usageStatus: {
                isUsed: false
            }
        };

        if (submodel.data?.fields && Array.isArray(submodel.data.fields)) {
            submodel.data.fields.forEach((field: any) => {
                this.registerComponent({
                    id: field.id,
                    name: field.name,
                    type: 'field',
                    fieldType: field.type,
                    isReusable: true,
                    parentId: submodelId,
                    dataConfiguration: field.dataConfiguration
                });
            });
        }

        if (submodel.data?.productCategories && Array.isArray(submodel.data.productCategories)) {
            submodel.data.productCategories.forEach((categoryWrapper: any) => {
                const categoriesData = categoryWrapper.data?.categories?.data;
                const categoryWrapperName = categoryWrapper.data?.name;
                const wrapperId = categoryWrapper.id;
                
                if (categoriesData && Array.isArray(categoriesData)) {
                    categoriesData.forEach((actualCategory: any) => {
                        this.registerComponent({
                            id: wrapperId, 
                            name: categoryWrapperName || actualCategory.name || 'Category',
                            type: 'category',
                            isReusable: true,
                            parentId: submodelId,
                            dataConfiguration: {
                                id: wrapperId,
                                categoryId: actualCategory.id,
                                categoryWrapper: categoryWrapper,
                                actualCategory: actualCategory,
                                wrapperName: categoryWrapperName,
                                actualCategoryName: actualCategory.name,
                                categoryType: categoryWrapper.data?.categories?.type,
                                ...categoryWrapper.dataConfiguration
                            }
                        });
                    });
                } else {
                    this.registerComponent({
                        id: wrapperId,
                        name: categoryWrapper.data?.name || 'Category',
                        type: 'category',
                        isReusable: true,
                        parentId: submodelId,
                        dataConfiguration: {
                            id: wrapperId, 
                            categoryId: null, 
                            categoryName: null,
                            wrapperName: categoryWrapper.data?.name,
                            categoryType: categoryWrapper.data?.categories?.type || '',
                            isEmpty: true, 
                            
                            categoryWrapper: categoryWrapper,
                            actualCategory: null,
                            ...categoryWrapper.data,
                            ...(categoryWrapper.dataConfiguration || {})
                        }
                    });
                }
            });
        }

        if (submodel.data?.connectors && Array.isArray(submodel.data.connectors)) {
            submodel.data.connectors.forEach((connector: any) => {
                this.registerComponent({
                    id: connector.id,
                    name: connector.data?.name || CONNECTOR,
                    type: 'connector',
                    isReusable: true,
                    parentId: submodelId,
                    dataConfiguration: connector.dataConfiguration
                });
            });
        }
    });

    this.registerBuiltInWidgets();
}

  private registerBuiltInWidgets(): void {
    const widgets = [
      {
        id: 'divider-widget',
        name: 'Divider',
        type: 'widget' as const,
        isReusable: true,
        widgetType: 'divider' as WidgetType
      },
      {
        id: 'content-block-widget',
        name: 'Content Block',
        type: 'widget' as const,
        isReusable: true,
        widgetType: 'contentBlock' as WidgetType
      },
      {
        id: 'your-build-widget',
        name: 'Your Build',
        type: 'widget' as const,
        isReusable: false,
        widgetType: 'yourBuild' as WidgetType
      },
      {
        id: 'button-widget',
        name: 'Button',
        type: 'widget' as const,
        isReusable: true,
        widgetType: 'button' as WidgetType
      },
      {
        id: 'summary-widget',
        name: 'Summary',
        type: 'widget' as const,
        isReusable: false,
        widgetType: 'summary' as WidgetType
      },
      {
        id: 'visualizer-widget',
        name: 'Visualizer',
        type: 'widget' as const,
        isReusable: false,
        widgetType: 'visualizer' as WidgetType
      }
    ];

    widgets.forEach(widget => {
      this.registerComponent(widget);
      
      this.registry.widgets[widget.id] = {
        type: widget.widgetType!,
        isReusable: widget.isReusable,
        usageStatus: {
          isUsed: false
        }
      };
    });
  }

  private registerComponent(component: ComponentDefinition): void {
    this.componentDefinitions.set(component.id, component);
    this.usageMap[component.id] = {
        isUsed: false
    };
  }

  resetRegistry(): void {
    this.usageMap = {};
    this.containerSubmodels.clear();
    this.globalSubmodelUsage.clear();
    this.containerWidgets.clear();
    this.containerHierarchy.clear();
    this.pageYourBuildWidget.clear();
    
    Object.keys(this.registry.subModels).forEach(id => {
      if (this.registry.subModels[id]) {
        this.registry.subModels[id].usageStatus = { isUsed: false };
      }
    });
    
    Object.keys(this.registry.widgets).forEach(id => {
      if (this.registry.widgets[id]) {
        this.registry.widgets[id].usageStatus = { isUsed: false };
      }
    });
  }
  canUseComponent(componentId: string, containerId: string, currentPageId?: string): boolean {
    const component = this.componentDefinitions.get(componentId);
  
    if (!component) {
      console.warn(`Component with ID ${componentId} not found in registry`);
      return false;
    }

    if (!this.usageMap[componentId]) {
      console.warn(`No usage info found for component ${componentId} - initializing`);
      this.usageMap[componentId] = { isUsed: false };
    }

    if (component.type === 'widget' && component.widgetType) {
      if (component.widgetType === 'divider') {
        return true;
      }
      
      if (component.widgetType === 'contentBlock') {
        return true;
      }
      
      if (component.widgetType === 'yourBuild') {
        
        if (currentPageId) {
          const result = this.canAddYourBuildToContainer(containerId, currentPageId);
          return result;
        } else {
          return true;
        }
      }
      
      if (!component.isReusable) {
        return !this.usageMap[componentId]?.isUsed;
      }

      return true;
    }

    if ((component.type === 'field' || component.type === 'category' || component.type === 'connector') && component.parentId) {
      const parentSubmodel = this.componentDefinitions.get(component.parentId);
      if (!parentSubmodel) {
        return false;
      }

      const isNested = containerStore.isContainerNested(containerId);
      const effectiveContainerId = isNested
        ? containerStore.getRootContainerId(containerId) 
        : containerId;

      const containerSubmodelId = this.containerSubmodels.get(effectiveContainerId);
      
      if (!containerSubmodelId) {
        if (this.globalSubmodelUsage.get(component.parentId)) {
          return false;
        }
        return true;
      }
      
      if (containerSubmodelId !== component.parentId) {
        return false;
      }
      
      const fieldUsage = this.usageMap[componentId];
      if (fieldUsage?.isUsed) {
        const currentRootId = isNested ? containerStore.getRootContainerId(containerId) : containerId;
        const fieldUsedInContainerId = fieldUsage.containerId;
        const fieldUsedRootId = containerStore.isContainerNested(fieldUsedInContainerId ?? '') 
          ? containerStore.getRootContainerId(fieldUsedInContainerId ?? '') 
          : fieldUsedInContainerId;
        
        if (currentRootId === fieldUsedRootId) {
          return false;
        }
        
        const descendantContainers = containerStore.getDescendantContainers(currentRootId);
        const containerFamily = [currentRootId, ...descendantContainers];
        
        if (containerFamily.includes(fieldUsedInContainerId ?? '')) {
          return false;
        }
      }
      
      return true;
    }
    
    if (component.type === 'submodel') {
      const isNested = containerStore.isContainerNested(containerId);
      
      if (isNested) {
        const rootContainerId = containerStore.getRootContainerId(containerId);
        const rootSubmodelId = this.containerSubmodels.get(rootContainerId);
        
        if (rootSubmodelId && rootSubmodelId !== componentId) {
          return false;
        }
        
        if (this.globalSubmodelUsage.get(componentId)) {
          return false;
        }
        
        return true;
      }
    
      if (this.globalSubmodelUsage.get(componentId)) {
        return false;
      }
      
      const containerSubmodelId = this.containerSubmodels.get(containerId);
      if (containerSubmodelId && containerSubmodelId !== componentId) {
        return false;
      }
      
      return true;
    }

    return true;
  }

  private isWidgetTypeUsedInContainer(widgetType: WidgetType, containerId: string): boolean {
    const containerWidgetTypes = this.containerWidgets.get(containerId);
    if (containerWidgetTypes) {
      return containerWidgetTypes.has(widgetType);
    }
    return false;
  }

  trackComponentUsage(componentId: string, location: ComponentLocation): void {
    const component = this.componentDefinitions.get(componentId);
    
    if (!component) {
      console.warn(`Cannot track usage for component with ID ${componentId} - not found in registry`);
      return;
    }

    const effectiveContainerId = containerStore.isContainerNested(location.containerId)
      ? containerStore.getRootContainerId(location.containerId) 
      : location.containerId;

    this.usageMap[componentId] = {
      isUsed: true,
      containerId: location.containerId,
      pageId: location.pageId,
      groupId: location.groupId
    };

    if (component.type === 'submodel') {
      this.registry.subModels[componentId].usageStatus = {
        isUsed: true,
        location
      };
  
      const rootContainerId = containerStore.isContainerNested(location.containerId) 
        ? containerStore.getRootContainerId(location.containerId)
        : location.containerId;
  
      this.containerSubmodels.set(rootContainerId, componentId);
  
      if (containerStore.isContainerNested(location.containerId)) {
        this.containerSubmodels.set(location.containerId, componentId);
      }
  
      this.globalSubmodelUsage.set(componentId, true);
    } else if (component.type === 'widget' && component.widgetType) {
      this.registry.widgets[componentId].usageStatus = {
        isUsed: true,
        location
      };
      
      // Special tracking for Your Build widget
      if (component.widgetType === 'yourBuild') {
        this.trackYourBuildOnPage(location.pageId, location.containerId, componentId);
      }
      
      if (!this.containerWidgets.has(location.containerId)) {
        this.containerWidgets.set(location.containerId, new Map());
      }
      
      const widgetCounts = this.containerWidgets.get(location.containerId)!;
      const currentCount = widgetCounts.get(component.widgetType) || 0;
      widgetCounts.set(component.widgetType, currentCount + 1);
    }

    if ((component.type === 'field' || component.type === 'category' || component.type === 'connector') && component.parentId) {
      this.usageMap[component.parentId] = {
        isUsed: true,
        containerId: effectiveContainerId,
        pageId: location.pageId,
        groupId: location.groupId
      };
      
      this.registry.subModels[component.parentId].usageStatus = {
        isUsed: true,
        location
      };
      
      this.containerSubmodels.set(effectiveContainerId, component.parentId);
      this.globalSubmodelUsage.set(component.parentId, true);
    }
  }

  releaseComponent(componentId: string, containerId: string): void {
  const component = this.componentDefinitions.get(componentId);
  
  if (!component) {
    console.warn(`Cannot release component with ID ${componentId} - not found in registry`);
    return;
  }

  if (!this.usageMap[componentId]) {
    console.warn(`No usage info found for component ${componentId} - initializing`);
    this.usageMap[componentId] = { isUsed: false };
    return;
  }

  if (
    this.usageMap[componentId].isUsed && 
    this.usageMap[componentId].containerId === containerId
  ) {
    this.usageMap[componentId] = {
      isUsed: false
    };

    if (component.type === 'submodel') {
      if (this.registry.subModels[componentId]) {
        this.registry.subModels[componentId].usageStatus = {
          isUsed: false
        };
      }
      
      this.containerSubmodels.delete(containerId);
      this.globalSubmodelUsage.delete(componentId);
      this.releaseSubmodelComponents(componentId, containerId);
    } else if (component.type === 'widget' && component.widgetType) {
      if (this.registry.widgets[componentId]) {
        this.registry.widgets[componentId].usageStatus = {
          isUsed: false
        };
      }
      
      if (component.widgetType === 'yourBuild') {
        const currentUsage = this.usageMap[componentId];
        if (currentUsage?.pageId) {
          this.removeYourBuildFromPage(currentUsage.pageId);
        }
      }
      
      const widgetCounts = this.containerWidgets.get(containerId);
      if (widgetCounts) {
        const currentCount = widgetCounts.get(component.widgetType) || 0;
        if (currentCount > 1) {
          widgetCounts.set(component.widgetType, currentCount - 1);
        } else {
          widgetCounts.delete(component.widgetType);
        }
      }

      // Clear saved config for button widgets on removal so new instances get default config
      if (component.widgetType === 'button') {
        this.clearWidgetConfig('button-widget');
      }
    }

    if ((component.type === 'field' || component.type === 'category' || component.type === 'connector') && component.parentId) {
      const otherComponentsFromSameSubmodel = Array.from(this.componentDefinitions.values())
        .filter(c => 
          (c.type === 'field' || c.type === 'category' || c.type === 'connector') && 
          c.parentId === component.parentId && 
          c.id !== componentId && 
          this.usageMap[c.id]?.isUsed && 
          this.usageMap[c.id]?.containerId === containerId
        );
      
      if (otherComponentsFromSameSubmodel.length === 0) {
        this.releaseComponent(component.parentId, containerId);
      }
    }
  }
}
  
  private releaseSubmodelComponents(submodelId: string, containerId: string): void {
  const componentsToRelease = Array.from(this.componentDefinitions.values())
    .filter(c => {
      
      if (c.type !== 'field' && c.type !== 'category' && c.type !== 'connector') {
        return false;
      }
      
      if (c.parentId !== submodelId) {
        return false;
      }
      
      const usageInfo = this.usageMap[c.id];
      if (!usageInfo) {
        console.warn(`No usage info found for component ${c.id} during release`);
        return false;
      }
      
      return usageInfo.isUsed && usageInfo.containerId === containerId;
    });
  
  componentsToRelease.forEach(component => {
    this.usageMap[component.id] = {
      isUsed: false
    };
  });
}

  getAvailableComponents(containerId: string, currentPageId?: string): ComponentDefinition[] {
    return Array.from(this.componentDefinitions.values())
      .filter(component => this.canUseComponent(component.id, containerId, currentPageId));
  }

  getUsageMap(): ComponentUsageMap {
    return this.usageMap;
  }

  getRegistry(): ComponentRegistryType {
    return this.registry;
  }

  getComponentDefinition(componentId: string): ComponentDefinition | undefined {
    return this.componentDefinitions.get(componentId);
  }

  getAllComponentsWithUsageInfo(containerId: string, currentPageId?: string): (ComponentDefinition & { usageStatus: { isUsed: boolean, isDisabled: boolean } })[] {
    
    const isNested = containerStore.isContainerNested(containerId);
    const rootContainerId = isNested ? containerStore.getRootContainerId(containerId) : containerId;
    const rootContainerSubmodelId = this.containerSubmodels.get(rootContainerId);
    
    const containerWidgetTypes = this.containerWidgets.get(containerId) || new Set();
    
    return Array.from(this.componentDefinitions.values()).map(component => {
      const isUsed = this.usageMap[component.id]?.isUsed || false;
      
      let isDisabled = false;
      
      if (component.type === 'submodel') {
        const isAlreadyUsed = this.globalSubmodelUsage.get(component.id) || false;
        const isDifferentSubmodelUsedInContainer = 
          rootContainerSubmodelId !== undefined && 
          rootContainerSubmodelId !== component.id;
          
        isDisabled = isAlreadyUsed || isDifferentSubmodelUsedInContainer;
      } 
      else if (component.type === 'field' || component.type === 'category' || component.type === 'connector') {
        if (component.parentId) {
          const fieldBelongsToContainerSubmodel = component.parentId === rootContainerSubmodelId;
          
          if (!rootContainerSubmodelId) {
            const parentIsUsedGlobally = this.globalSubmodelUsage.get(component.parentId) || false;
            isDisabled = parentIsUsedGlobally || isUsed;
          } else if (!fieldBelongsToContainerSubmodel) {
            isDisabled = true;
          } else if (isUsed) {
            const usedInContainerId = this.usageMap[component.id].containerId;
            const descendantContainers = containerStore.getDescendantContainers(rootContainerId);
            const containerFamily = [rootContainerId, ...descendantContainers];
            
            isDisabled = containerFamily.includes(usedInContainerId ?? '');
          } else {
            isDisabled = false;
          }
        }
      }
      else if (component.type === 'widget') {
        if (component.widgetType === 'divider') {
          isDisabled = false;
        } else if (component.widgetType === 'contentBlock') {
          isDisabled = containerWidgetTypes.has('contentBlock');
        } else if (component.widgetType === 'yourBuild') {
          // For Your Build widget, check if it can be used on current page
          if (currentPageId) {
            isDisabled = !this.canAddYourBuildToContainer(containerId, currentPageId);
          } else {
            isDisabled = !component.isReusable && isUsed;
          }
        } else {
          isDisabled = !component.isReusable && isUsed;
        }
      }
      
      return {
        ...component,
        usageStatus: {
          isUsed,
          isDisabled
        }
      };
    });
  }
  
  getContainerSubmodelId(containerId: string): string | undefined {
    return this.containerSubmodels.get(containerId);
  }
  
  isSubmodelUsedGlobally(submodelId: string): boolean {
    return this.globalSubmodelUsage.get(submodelId) || false;
  }

  setContainerParent(childId: string, parentId: string): void {
    this.containerHierarchy.set(childId, parentId);
  }

  removeContainerFromHierarchy(containerId: string): void {
    this.containerHierarchy.delete(containerId);
  }

  getRootContainerId(containerId: string): string {
    let currentId = containerId;
    let parentId = this.containerHierarchy.get(currentId);
    
    while (parentId) {
      currentId = parentId;
      parentId = this.containerHierarchy.get(currentId);
    }
    
    return currentId;
  }

  getDescendantContainers(parentId: string): string[] {
    const descendants: string[] = [];
    
    this.containerHierarchy.forEach((parent, child) => {
      if (this.getRootContainerId(child) === parentId || parent === parentId) {
        descendants.push(child);
      }
    });
    
    return descendants;
  }

  moveWidget(widgetId: string, fromContainerId: string, toContainerId: string, location: ComponentLocation): void {
    const component = this.componentDefinitions.get(widgetId);
    
    if (!component || component.type !== 'widget') {
      console.warn(`Cannot move non-widget component ${widgetId}`);
      return;
    }

    if (component.widgetType === 'yourBuild') {
      const currentUsage = this.usageMap[widgetId];
      if (currentUsage?.pageId === location.pageId) {
        this.moveYourBuildOnPage(location.pageId, toContainerId);
      } else {
        if (currentUsage?.pageId) {
          this.removeYourBuildFromPage(currentUsage.pageId);
        }
        this.trackYourBuildOnPage(location.pageId, location.containerId, widgetId);
      }
    }

    this.releaseComponent(widgetId, fromContainerId);
    
    this.trackComponentUsage(widgetId, location);
  }

  saveWidgetConfig(widgetId: string, config: any): void {
    this.moveableWidgetConfigs.set(widgetId, config);
  }

  getWidgetConfig(widgetId: string): any {
    return this.moveableWidgetConfigs.get(widgetId);
  }

  clearWidgetConfig(widgetId: string): void {
    this.moveableWidgetConfigs.delete(widgetId);
  }

  isWidgetInContainer(widgetId: string, containerId: string): boolean {
    const usage = this.usageMap[widgetId];
    return usage?.isUsed === true && usage?.containerId === containerId;
  }

  getWidgetContainer(widgetId: string): string | undefined {
    const usage = this.usageMap[widgetId];
    return usage?.isUsed ? usage.containerId : undefined;
  }

  canAddYourBuildToContainer(containerId: string, pageId: string): boolean {
    if (!pageId) return false;
    
    
    const currentPageWidget = this.pageYourBuildWidget.get(pageId);
    
    if (!currentPageWidget) {
      return true;
    }
    
    const canAdd = currentPageWidget.containerId === containerId;
    
    return canAdd;
  }

  getYourBuildContainerOnPage(pageId: string): string | null {
    if (!pageId) return null;
    
    const pageWidget = this.pageYourBuildWidget.get(pageId);
    return pageWidget ? pageWidget.containerId : null;
  }

  trackYourBuildOnPage(pageId: string, containerId: string, widgetId: string): void {
    if (!pageId) return;
    
    this.pageYourBuildWidget.set(pageId, { containerId, widgetId });
  }

  removeYourBuildFromPage(pageId: string): void {
    if (!pageId) return;
    
    this.pageYourBuildWidget.delete(pageId);
  }

  moveYourBuildOnPage(pageId: string, newContainerId: string): void {
    if (!pageId) return;
    
    const current = this.pageYourBuildWidget.get(pageId);
    if (current) {
      this.pageYourBuildWidget.set(pageId, { ...current, containerId: newContainerId });
    }
  }

  isYourBuildOnPage(pageId: string): boolean {
    if (!pageId) return false;
    
    return this.pageYourBuildWidget.has(pageId);
  }

  getYourBuildWidgetIdOnPage(pageId: string): string | null {
    if (!pageId) return null;
    
    const pageWidget = this.pageYourBuildWidget.get(pageId);
    return pageWidget ? pageWidget.widgetId : null;
  }

  clearYourBuildTracking(): void {
    this.pageYourBuildWidget.clear();
  }

}
